'use client';

import { Heart } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

interface CrisisModalProps {
  open: boolean;
  onClose: () => void;
  testIdPrefix?: string;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Splits `text` on each phrase in `boldPhrases` and wraps the matched phrases
// in <strong>. The concatenated text content is unchanged from `text`.
function renderWithBoldPhrases(text: string, boldPhrases: readonly string[]): React.ReactNode {
  const phrases = boldPhrases.filter((phrase) => phrase.length > 0);
  if (phrases.length === 0) return text;

  const pattern = new RegExp(`(${phrases.map(escapeRegExp).join('|')})`, 'g');
  const segments = text.split(pattern);

  return segments.map((segment, index) =>
    phrases.includes(segment) ? (
      <strong key={`${index}-${segment}`} className="font-semibold text-foreground">
        {segment}
      </strong>
    ) : (
      segment
    ),
  );
}

export function CrisisModal({ open, onClose, testIdPrefix = 'crisis-modal' }: CrisisModalProps) {
  const { t } = useLanguage();

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-testid={`${testIdPrefix}-root`}
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Heart className="text-primary" size={22} />
          </div>
          <h3 className="text-base font-semibold text-foreground">{t.crisis.title}</h3>
        </div>
        <div className="flex gap-3 mb-4">
          <a
            href="https://988lifeline.org"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition text-center"
            data-testid={`${testIdPrefix}-get-help-link`}
          >
            {t.crisis.getHelp}
          </a>
          <a
            href="https://findahelpline.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 px-3 py-2 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary/10 transition text-center"
            data-testid={`${testIdPrefix}-find-resources-link`}
          >
            {t.crisis.findResources}
          </a>
        </div>
        <p
          className="text-sm text-foreground-muted mb-5 whitespace-pre-wrap"
          data-testid={`${testIdPrefix}-advisory`}
        >
          {renderWithBoldPhrases(t.crisis.advisory, t.crisis.advisoryBold)}
        </p>
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-sm font-medium text-foreground hover:bg-background-secondary rounded-lg transition"
          data-testid={`${testIdPrefix}-cancel-btn`}
        >
          {t.common.cancel}
        </button>
      </div>
    </div>
  );
}
